
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUpcomingEvents,
  createUpcomingEvent,
  updateUpcomingEvent,
  deleteUpcomingEvent,
} from "../../api/controller/admin/adminUpcommingEventsController";
import AdminUpcommingEventsDialog from "./adminDialogBoxes/AdminUpcommingEventsDialog";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { useFlashMessage } from "../../components/FlashMessageProvider";

const AdminUpcomingEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { showMessage } = useFlashMessage();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchUpcomingEvents({});
        if (mounted) setEvents(res || []);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load events.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  const refresh = async () => {
    try {
      const res = await fetchUpcomingEvents({});
      setEvents(res || []);
    } catch (err) {
      console.error(err);
      setError("Failed to refresh events.");
    }
  };

  const handleAdd = () => {
    setDialogInitial(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setDialogInitial(item);
    setDialogOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      const isEditing = Boolean(dialogInitial && (dialogInitial._id || dialogInitial.id));
      if (isEditing) {
        await updateUpcomingEvent(dialogInitial._id || dialogInitial.id, formData);
      } else {
        await createUpcomingEvent(formData);
      }
      await refresh();
      showMessage(isEditing ? 'Event updated successfully.' : 'Event created successfully.', { type: 'success' });
      setDialogOpen(false);
      setDialogInitial(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to save event.', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = (item) => {
    setDeleteTarget({ id: item._id || item.id, title: item.title });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteUpcomingEvent(deleteTarget.id);
      await refresh();
      showMessage('Event deleted.', { type: 'success' });
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to delete event.', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Admin — Upcoming Events</h1>

      <div className="mb-4">
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded">Add Event</button>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading events...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => (
            <div key={ev._id || ev.id} className="bg-white p-4 rounded shadow flex items-start gap-4">
              {ev.image ? (
                <div className="w-32 h-20 flex-shrink-0 overflow-hidden rounded">
                  <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{ev.title}</h3>
                <p className="text-sm text-gray-600">{ev.startDateTime ? new Date(ev.startDateTime).toLocaleDateString() : String(ev.date)} • {ev.startDateTime ? new Date(ev.startDateTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ev.startTime}{ev.endDateTime ? ` - ${new Date(ev.endDateTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}` : (ev.endTime ? ` - ${ev.endTime}` : '')} • {ev.location}</p>
                <p className="mt-2 text-gray-700">{ev.description}</p>
                <div className="mt-3">
                  <button onClick={() => handleEdit(ev)} className="text-blue-600 mr-4">Edit</button>
                  <button onClick={() => handleDeleteRequest(ev)} className="text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminUpcommingEventsDialog
        open={dialogOpen}
        initial={dialogInitial}
        onClose={() => {
          if (isSaving) return;
          setDialogOpen(false);
          setDialogInitial(null);
        }}
        onSave={handleSave}
        isSaving={isSaving}
      />
      <AdminConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Event"
        message={deleteTarget?.title ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : 'Are you sure you want to delete this event?'}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default AdminUpcomingEvents;
